import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import SectorPerformance from './SectorPerformance';
import fetchIndustryPerformance, {
  getDefault as getDefaultIndustry,
  getIndustryPerformanceQuery,
} from '@/pages/Market/query/fetchIndustryPerformance';
import { formatterFactor } from '@/pages/Market/utils/formatter';
import { BarChart } from './BarChart';
import useReduxStateMarket, {
  MarketPageType,
} from '@/pages/Market/hooks/useReduxStateMarkets';
import useIndustryGetter from '@/pages/Market/hooks/useIndustryGetter';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import { Feedback } from '../CompanyFeeds/Feedback';
import { Row, Button } from '@simplywallst/ui-core';
import { format, parse } from 'date-fns';
import { getReadableCountry } from '@/utilities/markets';
import loadable from '@loadable/component';
import If from '@components/If';
import { useIndustryMetrics } from '@/hooks/useIndustryMetrics';

const LearnMoreModal = loadable(() => import('../LearnMoreModal'));
const SectorTrendsChartModal = loadable(
  () => import('../LearnMoreModal/SectorTrendsChartModal')
);

export const Sectors = () => {
  const { market, sector, secondary, tertiary, marketPageType } =
    useReduxStateMarket();
  const [{ id: industryId }, enabled] = useIndustryGetter();
  const { data: industryPerformanceData = getDefaultIndustry(), isFetching } =
    useQuery({
      queryKey: getIndustryPerformanceQuery(
        market,
        industryId,
        sector || '',
        secondary || '',
        tertiary || ''
      ),
      queryFn: fetchIndustryPerformance,
      enabled: marketPageType === MarketPageType.MARKET ? true : enabled,
      staleTime: Infinity,
      refetchOnMount: false,
    });

  const { latestAnalysis } = useIndustryMetrics(
    market,
    industryId,
    marketPageType === MarketPageType.MARKET ? true : enabled
  );

  const getData = useCallback(
    (tab, period) => {
      const dataTab =
        industryPerformanceData && industryPerformanceData[tab]
          ? industryPerformanceData[tab]
          : [];
      return tab === 'pe' ? dataTab : dataTab[period];
    },
    [industryPerformanceData]
  );

  // for learn more modal
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const open = () => setIsVisible(true);
  const close = () => setIsVisible(false);
  const getModalData = useCallback(() => {
    const title = `About the ${!sector ? 'Sector' : 'Industry'} Trends Chart`;
    const content = latestAnalysis.analysisDate ? (
      <SectorTrendsChartModal
        isSector={!sector}
        market={getReadableCountry(market)}
        totalCompany={latestAnalysis.companyCount}
        latestPE={formatterFactor(latestAnalysis?.marketPE)}
        analysisDate={format(
          parse(
            latestAnalysis?.analysisDate as string,
            'yyyy-MM-dd',
            new Date()
          ),
          'MMM dd yyyy'
        )}
      />
    ) : (
      <div />
    );

    return { title, content };
  }, [latestAnalysis, market, sector]);

  return (
    <>
      <SectorPerformance
        isSector={!sector}
        disabled={isFetching}
        isShowingSecondTabHeader={
          !isFetching && industryPerformanceData['pe'].length > 0
        }
      >
        {({ tab, period }) => (
          <BarChart data={getData(tab, period)} tab={tab} />
        )}
      </SectorPerformance>
      <Row justifyContent="flex-end">
        <Button
          data-cy-id="sector-performance-learn-more-modal-btn"
          styleType="passive"
          alignIcon="right"
          onClick={open}
        >
          <InformationSolid /> Learn
        </Button>
        <Feedback formId={'bxeqC9jR'} />
      </Row>
      <If match={isVisible}>
        {() => {
          const { title, content } = getModalData();
          return (
            <LearnMoreModal close={close} title={title}>
              {content}
            </LearnMoreModal>
          );
        }}
      </If>
    </>
  );
};

export default Sectors;
